import React from "react";
import { ArrowRight, CheckCircle, Rocket, Globe, Smartphone, Zap, Target, Users, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import OurProcess from "@/components/ui/OurProcess";
import { mock } from "@/mock";

export default function MvpRapidPrototyping() {
  const navigate = useNavigate();
  const service = mock.services.find(s => s.id === "mvp-rapid");

  const mvpProcessSteps = [
    {
      number: "01",
      title: "Idea Validation",
      description: "Market research & feature prioritization"
    },
    {
      number: "02", 
      title: "Wireframes & Prototypes",
      description: "Interactive designs & user flows"
    },
    {
      number: "03",
      title: "MVP Development", 
      description: "Rapid iterations & core feature building"
    },
    {
      number: "04",
      title: "User Feedback & Iteration",
      description: "Analytics integration & continuous improvement"
    }
  ];

  if (!service) return null;

  return (
    <div className="mx-auto max-w-[1200px] px-5 pt-2 pb-16">
      {/* Hero Section with Overlay Image */}
      <>
        <div className="relative mb-4 rounded-3xl overflow-hidden">
          {/* Background Image with Overlay */}
          <div className="relative h-[500px] animate-in fade-in duration-1000">
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/70 z-10"></div>
            <img 
              src="/MVP.png" 
              alt="MVP & Rapid Prototyping" 
              className="absolute inset-0 w-full h-full object-cover opacity-85"
            />
            {/* Animated Particles Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-purple-500/20 animate-pulse z-[5]"></div>
            
            {/* Content Overlay */}
            <div className="relative z-20 h-full flex flex-col justify-center px-8 md:px-16">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-xs text-primary mb-6 w-fit animate-in slide-in-from-left-8 duration-700">
                <Rocket className="h-4 w-4" />
                MVP & Rapid Prototyping
              </div>
              <h1 className="text-4xl font-bold tracking-tight text-white md:text-6xl max-w-3xl animate-in slide-in-from-left-8 duration-700 delay-100">
                {service.title}
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/90 animate-in slide-in-from-left-8 duration-700 delay-200">
                Quickly validate your product ideas and get to market faster with our MVP and rapid prototyping services. We help you build, test, and iterate on core features to attract early adopters and investors.
              </p>
            </div>
          </div>
        </div>
      </>

      {/* Key Features */}
      <>
        <div className="mt-4 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card className="border-white/10 bg-gradient-to-br from-primary/5 to-transparent backdrop-blur-sm">
            <CardContent className="pt-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <Zap className="h-6 w-6 text-primary" />
              </div>
              <h3 className="mb-2 font-semibold text-white">Fast Time-to-Market</h3>
              <p className="text-sm text-white/60">Launch in weeks, not months</p>
            </CardContent>
          </Card>
          <Card className="border-white/10 bg-gradient-to-br from-primary/5 to-transparent backdrop-blur-sm">
            <CardContent className="pt-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <Target className="h-6 w-6 text-primary" />
              </div>
              <h3 className="mb-2 font-semibold text-white">Focused Features</h3>
              <p className="text-sm text-white/60">Core functionality first</p>
            </CardContent>
          </Card>
          <Card className="border-white/10 bg-gradient-to-br from-primary/5 to-transparent backdrop-blur-sm">
            <CardContent className="pt-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h3 className="mb-2 font-semibold text-white">User Validation</h3>
              <p className="text-sm text-white/60">Real feedback early</p>
            </CardContent>
          </Card>
          <Card className="border-white/10 bg-gradient-to-br from-primary/5 to-transparent backdrop-blur-sm">
            <CardContent className="pt-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <TrendingUp className="h-6 w-6 text-primary" />
              </div>
              <h3 className="mb-2 font-semibold text-white">Iterative Growth</h3>
              <p className="text-sm text-white/60">Data-driven improvements</p>
            </CardContent>
          </Card>
        </div>
      </>

      <Separator className="my-16 bg-white/10" />

      {/* Our Process Section */}
      <>
        <OurProcess 
          steps={mvpProcessSteps}
          title="Our MVP Development Process"
          subtitle="Fast-track your idea from concept to market-ready product"
        />
      </>

      <Separator className="my-16 bg-white/10" />

      <div className="grid gap-12 lg:grid-cols-2">
        {/* Problems We Solve */}
        <>
          <Card className="border-white/10 bg-black/40 backdrop-blur-md">
            <CardHeader className="pb-4">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-red-400" />
                Problems We Solve
              </h2>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {service.problems.map((problem, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white/70">
                    <div className="h-2 w-2 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                    {problem}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </>

        {/* Deliverables */}
        <>
          <Card className="border-white/10 bg-black/40 backdrop-blur-md">
            <CardHeader className="pb-4">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Rocket className="h-5 w-5 text-green-400" />
                What You Get
              </h2>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {service.deliverables.map((deliverable, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white/70">
                    <div className="h-2 w-2 rounded-full bg-green-400 mt-2 flex-shrink-0" />
                    {deliverable}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </>
      </div>

     

      {/* CTA */}
      <>
        <div className="mt-16 text-center">
          <div className="rounded-3xl border border-white/10 bg-white/[0.05] p-8 backdrop-blur-md">
            <h3 className="text-2xl font-semibold text-white mb-4">
              Ready to Build Your MVP?
            </h3>
            <p className="text-white/70 mb-6 max-w-2xl mx-auto">
              Let's validate your idea and create a prototype that captures investor attention.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => window.open("https://cal.com/vadri-2cqnbp", "_blank")}
                className="h-[52px] rounded-xl bg-primary px-6 text-black btn-glow hover:bg-primary/90"
              >
                Get a Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate("/contact")}
                className="h-[52px] rounded-xl border border-white/15 bg-white/[0.06] text-white hover:bg-white/10"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </>
    </div>
  );
}
